package com.videoanalyzer.web.components

import androidx.compose.runtime.Composable
import com.videoanalyzer.web.models.CreditPack
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Pricing page with Free and Pro tiers.
 *
 * ANDROID EQUIVALENT: Subscription screen in Google Play billing
 */
@Composable
fun PricingScreen(
    currentTier: String,
    analysesUsed: Int,
    creditBalance: Int = 0,
    isLoading: Boolean,
    onUpgrade: () -> Unit,
    onManageSubscription: () -> Unit,
    onBuyCredits: (String) -> Unit = {}
) {
    val isPro = currentTier == "pro"

    Div({
        classes("fade-in")
        style {
            maxWidth(1000.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
            padding(32.px, 24.px)
        }
    }) {
        // Header with animated gradient
        Div({
            classes("fade-in-up")
            style {
                textAlign("center")
                marginBottom(56.px)
            }
        }) {
            // Icon
            Div({
                style {
                    fontSize(56.px)
                    marginBottom(20.px)
                }
            }) { Text("💎") }

            H1({
                style {
                    fontSize(36.px)
                    fontWeight(800)
                    marginBottom(12.px)
                    color(Color("#FAFAFA"))
                }
            }) { Text("Choose Your Plan") }

            P({
                style {
                    color(Color("#A1A1AA"))
                    fontSize(17.px)
                    maxWidth(500.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                    lineHeight("1.6")
                }
            }) {
                Text("Unlock unlimited analyses and take your YouTube game to the next level")
            }
        }

        // Pricing cards
        Div({
            classes("fade-in-up", "stagger-1")
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(320px, 1fr))")
                gap(28.px)
                alignItems(AlignItems.Start)
                marginBottom(48.px)
            }
        }) {
            // Free tier
            PricingTier(
                name = "Free",
                price = "€0",
                period = "/month",
                description = "Get a taste of the analyzer",
                features = listOf(
                    "1 full analysis per month",
                    "Scores only on additional videos",
                    "Title & thumbnail scoring",
                    "Overall performance score"
                ),
                isCurrentPlan = !isPro,
                isFeatured = false,
                buttonText = if (!isPro) "Current Plan" else "Downgrade",
                onAction = { /* No action for free */ }
            )

            // Pro tier - Featured with red glow
            PricingTier(
                name = "Pro",
                price = "€19",
                period = "/month",
                description = "For serious content creators",
                features = listOf(
                    "20 full analyses per day",
                    "All recommendations unlocked",
                    "15+ analysis categories",
                    "Actionable improvement tips",
                    "Priority support"
                ),
                isCurrentPlan = isPro,
                isFeatured = true,
                buttonText = when {
                    isPro -> "Manage Subscription"
                    isLoading -> "Loading..."
                    else -> "Upgrade to Pro"
                },
                onAction = {
                    if (isPro) onManageSubscription() else onUpgrade()
                },
                isLoading = isLoading
            )
        }

        // Credit packs section (only show for non-Pro users)
        if (!isPro) {
            CreditPacksSection(
                currentBalance = creditBalance,
                isLoading = isLoading,
                onBuyCredits = onBuyCredits
            )
        }

        // FAQ section
        FaqSection()
    }
}

/**
 * Credit packs section for one-time purchases.
 */
@Composable
private fun CreditPacksSection(
    currentBalance: Int,
    isLoading: Boolean,
    onBuyCredits: (String) -> Unit
) {
    Div({
        classes("fade-in-up", "stagger-2")
        style {
            marginTop(56.px)
            textAlign("center")
        }
    }) {
        // Section header
        Div({
            style {
                marginBottom(32.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(12.px)
                    marginBottom(12.px)
                }
            }) {
                Span({
                    style {
                        fontSize(32.px)
                    }
                }) { Text("🎟️") }

                H2({
                    style {
                        fontSize(28.px)
                        fontWeight(700)
                        color(Color("#FAFAFA"))
                    }
                }) { Text("Or Buy Credits") }
            }

            P({
                style {
                    color(Color("#A1A1AA"))
                    fontSize(15.px)
                }
            }) { Text("Pay as you go - no subscription required") }

            // Current balance badge - neutral styling
            if (currentBalance > 0) {
                Div({
                    style {
                        marginTop(16.px)
                        display(DisplayStyle.InlineBlock)
                        padding(10.px, 20.px)
                        backgroundColor(Color("rgba(255, 255, 255, 0.05)"))
                        borderRadius(6.px)
                        property("border", "1px solid rgba(255, 255, 255, 0.1)")
                    }
                }) {
                    Span({
                        style {
                            color(Color("#FAFAFA"))
                            fontSize(15.px)
                            fontWeight(600)
                        }
                    }) { Text("💰 Current balance: $currentBalance credits") }
                }
            }
        }

        // Credit pack cards
        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                gap(20.px)
                maxWidth(750.px)
                margin(0.px, "auto".unsafeCast<CSSNumeric>())
            }
        }) {
            CreditPackCard(
                packId = "pack_10",
                credits = 10,
                price = "€7",
                pricePerCredit = "€0.70",
                isLoading = isLoading,
                onBuy = onBuyCredits
            )
            CreditPackCard(
                packId = "pack_25",
                credits = 25,
                price = "€15",
                pricePerCredit = "€0.60",
                isMostPopular = true,
                isLoading = isLoading,
                onBuy = onBuyCredits
            )
            CreditPackCard(
                packId = "pack_50",
                credits = 50,
                price = "€25",
                pricePerCredit = "€0.50",
                isLoading = isLoading,
                onBuy = onBuyCredits
            )
        }
    }
}

@Composable
private fun CreditPackCard(
    packId: String,
    credits: Int,
    price: String,
    pricePerCredit: String,
    isMostPopular: Boolean = false,
    isLoading: Boolean,
    onBuy: (String) -> Unit
) {
    Div({
        classes("card", "scale-in")
        style {
            padding(28.px)
            textAlign("center")
            position(Position.Relative)
            if (isMostPopular) {
                property("border", "2px solid #FF0000")
            }
        }
    }) {
        // Best value badge - red background with white text
        if (isMostPopular) {
            Div({
                style {
                    position(Position.Absolute)
                    top((-12).px)
                    left(50.percent)
                    property("transform", "translateX(-50%)")
                    backgroundColor(Color("#FF0000"))
                    color(Color.white)
                    padding(6.px, 16.px)
                    borderRadius(4.px)
                    fontSize(11.px)
                    fontWeight(700)
                    letterSpacing(0.5.px)
                }
            }) { Text("MOST POPULAR") }
        }

        // Credits - white text
        Div({
            style {
                marginTop(if (isMostPopular) 8.px else 0.px)
            }
        }) {
            Div({
                style {
                    fontSize(42.px)
                    fontWeight(800)
                    color(Color("#FAFAFA"))
                    fontFamily("'SF Mono', Monaco, monospace")
                }
            }) { Text("$credits") }

            Div({
                style {
                    fontSize(14.px)
                    color(Color("#9CA3AF"))
                    marginBottom(16.px)
                    fontWeight(500)
                }
            }) { Text("credits") }
        }

        // Price with highlight
        Div({
            style {
                padding(12.px)
                backgroundColor(Color("#27272A"))
                borderRadius(8.px)
                marginBottom(16.px)
            }
        }) {
            Div({
                style {
                    fontSize(28.px)
                    fontWeight(700)
                    color(Color("#FAFAFA"))
                    marginBottom(4.px)
                }
            }) { Text(price) }

            Div({
                style {
                    fontSize(12.px)
                    color(Color("#9CA3AF"))
                }
            }) { Text("$pricePerCredit per analysis") }
        }

        // Buy button - red for recommended, outline for others
        Button({
            classes("btn")
            style {
                width(100.percent)
                padding(12.px)
                fontSize(14.px)
                fontWeight(600)
                borderRadius(6.px)
                if (isMostPopular) {
                    backgroundColor(Color("#FF0000"))
                    color(Color.white)
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.25)")
                } else {
                    backgroundColor(Color.transparent)
                    color(Color("#FAFAFA"))
                    property("border", "1px solid rgba(255, 255, 255, 0.2)")
                }
            }
            onClick { onBuy(packId) }
            if (isLoading) disabled()
        }) {
            if (isLoading) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(8.px)
                    }
                }) {
                    InlineLoading()
                    Text("Processing...")
                }
            } else {
                Span({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(8.px)
                    }
                }) {
                    Text("Buy Now")
                    if (isMostPopular) Text("→")
                }
            }
        }
    }
}

@Composable
private fun PricingTier(
    name: String,
    price: String,
    period: String,
    description: String,
    features: List<String>,
    isCurrentPlan: Boolean,
    isFeatured: Boolean,
    buttonText: String,
    onAction: () -> Unit,
    isLoading: Boolean = false
) {
    Div({
        classes("card", "scale-in")
        style {
            padding(36.px)
            position(Position.Relative)
            if (isFeatured) {
                property("border", "2px solid #FF0000")
                property("box-shadow", "0 0 40px rgba(255, 0, 0, 0.15)")
            }
        }
    }) {
        // Featured badge - red background with white text
        if (isFeatured) {
            Div({
                style {
                    position(Position.Absolute)
                    top((-14).px)
                    left(50.percent)
                    property("transform", "translateX(-50%)")
                    backgroundColor(Color("#FF0000"))
                    color(Color.white)
                    padding(8.px, 24.px)
                    borderRadius(50.px)
                    fontSize(11.px)
                    fontWeight(700)
                    letterSpacing(1.px)
                }
            }) { Text("MOST POPULAR") }
        }

        // Plan icon and name
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(12.px)
                marginBottom(16.px)
                marginTop(if (isFeatured) 8.px else 0.px)
            }
        }) {
            Div({
                style {
                    width(44.px)
                    height(44.px)
                    borderRadius(8.px)
                    backgroundColor(Color("#27272A"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(22.px)
                }
            }) { Text(if (isFeatured) "⭐" else "🆓") }

            H2({
                style {
                    fontSize(26.px)
                    fontWeight(700)
                    color(Color("#FAFAFA"))
                }
            }) { Text(name) }
        }

        // Price with period - white for all
        Div({
            style {
                marginBottom(12.px)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Baseline)
                gap(4.px)
            }
        }) {
            Span({
                style {
                    fontSize(52.px)
                    fontWeight(800)
                    fontFamily("'SF Mono', Monaco, monospace")
                    color(Color("#FAFAFA"))
                }
            }) { Text(price) }
            Span({
                style {
                    fontSize(16.px)
                    color(Color("#9CA3AF"))
                    fontWeight(500)
                }
            }) { Text(period) }
        }

        // Description
        P({
            style {
                color(Color("#9CA3AF"))
                marginBottom(28.px)
                fontSize(15.px)
                lineHeight("1.5")
            }
        }) { Text(description) }

        // Features list - white checkmarks
        Ul({
            style {
                listStyleType("none")
                padding(0.px)
                marginBottom(28.px)
            }
        }) {
            features.forEach { feature ->
                Li({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(14.px)
                        marginBottom(14.px)
                        color(Color("#FAFAFA"))
                        fontSize(15.px)
                    }
                }) {
                    Div({
                        style {
                            width(22.px)
                            height(22.px)
                            borderRadius(6.px)
                            backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            flexShrink(0)
                        }
                    }) {
                        Span({
                            style {
                                color(Color("#FAFAFA"))
                                fontSize(12.px)
                                fontWeight(700)
                            }
                        }) { Text("✓") }
                    }
                    Text(feature)
                }
            }
        }

        // Action button - red for Pro CTA, outline for free
        Button({
            classes("btn")
            style {
                width(100.percent)
                padding(16.px)
                fontSize(16.px)
                fontWeight(600)
                borderRadius(10.px)
                if (isFeatured && !isCurrentPlan) {
                    backgroundColor(Color("#FF0000"))
                    color(Color.white)
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.25)")
                } else {
                    backgroundColor(Color.transparent)
                    color(Color("#FAFAFA"))
                    property("border", "1px solid rgba(255, 255, 255, 0.2)")
                }
                cursor(if (isCurrentPlan && !isFeatured) "default" else "pointer")
                property("transition", "all 0.2s ease")
            }
            if ((isCurrentPlan && !isFeatured) || isLoading) {
                disabled()
                style { opacity(0.6) }
            }
            onClick { onAction() }
        }) {
            if (isLoading) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(10.px)
                    }
                }) {
                    InlineLoading()
                    Text("Processing...")
                }
            } else {
                Span({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        gap(8.px)
                    }
                }) {
                    Text(buttonText)
                    if (!isCurrentPlan && isFeatured) {
                        Text("→")
                    }
                }
            }
        }

        // Current plan indicator - subtle white/gray
        if (isCurrentPlan) {
            Div({
                style {
                    marginTop(16.px)
                    textAlign("center")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(8.px)
                }
            }) {
                Div({
                    style {
                        width(8.px)
                        height(8.px)
                        borderRadius(50.percent)
                        backgroundColor(Color("#FAFAFA"))
                    }
                })
                Span({
                    style {
                        fontSize(14.px)
                        color(Color("#9CA3AF"))
                        fontWeight(500)
                    }
                }) { Text("Your current plan") }
            }
        }
    }
}

@Composable
private fun FaqSection() {
    Div({
        classes("fade-in-up", "stagger-3")
        style {
            marginTop(72.px)
        }
    }) {
        // Section header
        Div({
            style {
                textAlign("center")
                marginBottom(36.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(12.px)
                    marginBottom(12.px)
                }
            }) {
                Span({
                    style { fontSize(28.px) }
                }) { Text("❓") }

                H2({
                    style {
                        fontSize(26.px)
                        fontWeight(700)
                        color(Color("#FAFAFA"))
                    }
                }) { Text("Frequently Asked Questions") }
            }

            P({
                style {
                    color(Color("#A1A1AA"))
                    fontSize(15.px)
                }
            }) { Text("Everything you need to know about our pricing") }
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(400px, 1fr))")
                gap(20.px)
            }
        }) {
            FaqItem(
                icon = "🎯",
                question = "How does the video analysis work?",
                answer = "Simply paste your YouTube video URL and our AI will analyze your title and thumbnail. You'll get a score out of 100 along with specific recommendations to improve your click-through rate."
            )
            FaqItem(
                icon = "📈",
                question = "Will this actually improve my views?",
                answer = "Our AI analyzes what makes top-performing videos successful and applies those insights to yours. Creators typically see improved click-through rates after implementing our title and thumbnail recommendations."
            )
            FaqItem(
                icon = "💳",
                question = "What's the difference between credits and Pro?",
                answer = "Credits are one-time purchases that never expire - great for occasional use. Pro gives you 20 full analyses per day with a monthly subscription, perfect for active creators."
            )
            FaqItem(
                icon = "🔄",
                question = "Can I cancel my subscription anytime?",
                answer = "Yes! You can cancel anytime. You'll keep Pro access until the end of your billing period, and any unused credits remain in your account."
            )
        }
    }
}

@Composable
private fun FaqItem(icon: String, question: String, answer: String) {
    Div({
        classes("card")
        style {
            padding(24.px)
            property("transition", "all 0.3s ease")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.FlexStart)
                gap(16.px)
            }
        }) {
            // Icon container
            Div({
                style {
                    width(40.px)
                    height(40.px)
                    borderRadius(8.px)
                    backgroundColor(Color("#27272A"))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(18.px)
                    flexShrink(0)
                }
            }) { Text(icon) }

            Div {
                H3({
                    style {
                        fontSize(16.px)
                        fontWeight(600)
                        color(Color("#FAFAFA"))
                        marginBottom(10.px)
                        lineHeight("1.4")
                    }
                }) { Text(question) }

                P({
                    style {
                        color(Color("#A1A1AA"))
                        fontSize(14.px)
                        lineHeight("1.7")
                        margin(0.px)
                    }
                }) { Text(answer) }
            }
        }
    }
}

/**
 * Usage limit reached screen.
 */
@Composable
fun LimitReachedScreen(
    currentUsage: Int,
    limit: Int,
    creditBalance: Int = 0,
    isLoading: Boolean,
    onUpgrade: () -> Unit,
    onBuyCredits: (String) -> Unit = {},
    onBack: () -> Unit
) {
    Div({
        classes("fade-in")
        style {
            maxWidth(550.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
            padding(48.px, 24.px)
            textAlign("center")
        }
    }) {
        // Warning icon
        Div({
            classes("scale-in")
            style {
                fontSize(72.px)
                marginBottom(24.px)
            }
        }) { Text("⚠️") }

        H1({
            style {
                fontSize(28.px)
                fontWeight(800)
                marginBottom(12.px)
                color(Color("#FAFAFA"))
            }
        }) { Text("Analysis Limit Reached") }

        P({
            style {
                color(Color("#A1A1AA"))
                marginBottom(32.px)
                fontSize(16.px)
                lineHeight("1.6")
            }
        }) {
            Text("You've used all $limit free analyses this month.")
        }

        // Usage indicator
        Div({
            classes("fade-in-up")
            style {
                marginBottom(32.px)
                padding(20.px)
                backgroundColor(Color("rgba(245, 158, 11, 0.1)"))
                borderRadius(16.px)
                property("border", "1px solid rgba(245, 158, 11, 0.2)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(8.px)
                    marginBottom(12.px)
                }
            }) {
                Span({
                    style {
                        fontSize(32.px)
                        fontWeight(800)
                        color(Color("#f59e0b"))
                    }
                }) { Text("$currentUsage") }
                Span({
                    style {
                        fontSize(18.px)
                        color(Color("#64748b"))
                    }
                }) { Text("/ $limit") }
            }

            // Progress bar
            Div({
                style {
                    height(8.px)
                    backgroundColor(Color("#27272A"))
                    borderRadius(4.px)
                    property("overflow", "hidden")
                }
            }) {
                Div({
                    style {
                        width(100.percent)
                        height(100.percent)
                        backgroundColor(Color("#F59E0B"))
                        borderRadius(4.px)
                    }
                })
            }
        }

        // Upgrade card - red border
        Div({
            classes("card", "fade-in-up", "stagger-1")
            style {
                textAlign("left")
                marginBottom(24.px)
                padding(28.px)
                property("border", "1px solid rgba(255, 0, 0, 0.3)")
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    marginBottom(16.px)
                }
            }) {
                Span({
                    style { fontSize(28.px) }
                }) { Text("⭐") }

                H3({
                    style {
                        fontSize(20.px)
                        fontWeight(700)
                        color(Color("#FAFAFA"))
                    }
                }) { Text("Upgrade to Pro") }
            }

            P({
                style {
                    color(Color("#9CA3AF"))
                    marginBottom(20.px)
                    fontSize(15.px)
                }
            }) {
                Text("Get 20 full analyses per day for just €19/month")
            }

            Ul({
                style {
                    listStyleType("none")
                    padding(0.px)
                    marginBottom(20.px)
                }
            }) {
                listOf(
                    "20 full analyses per day",
                    "All recommendations unlocked",
                    "Priority support"
                ).forEach { feature ->
                    Li({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(10.px)
                            marginBottom(10.px)
                            color(Color("#FAFAFA"))
                            fontSize(14.px)
                        }
                    }) {
                        Div({
                            style {
                                width(20.px)
                                height(20.px)
                                borderRadius(4.px)
                                backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                            }
                        }) {
                            Span({
                                style {
                                    color(Color("#FAFAFA"))
                                    fontSize(11.px)
                                    fontWeight(700)
                                }
                            }) { Text("✓") }
                        }
                        Text(feature)
                    }
                }
            }

            Button({
                classes("btn")
                style {
                    width(100.percent)
                    padding(14.px)
                    fontSize(16.px)
                    fontWeight(600)
                    backgroundColor(Color("#FF0000"))
                    color(Color.white)
                    property("box-shadow", "0 4px 20px rgba(255, 0, 0, 0.25)")
                    borderRadius(8.px)
                }
                onClick { onUpgrade() }
                if (isLoading) disabled()
            }) {
                if (isLoading) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            gap(10.px)
                        }
                    }) {
                        InlineLoading()
                        Text("Processing...")
                    }
                } else {
                    Span({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            gap(8.px)
                        }
                    }) {
                        Text("Upgrade Now")
                        Text("→")
                    }
                }
            }
        }

        // Or buy credits section
        Div({
            classes("fade-in-up", "stagger-2")
            style {
                marginBottom(32.px)
            }
        }) {
            // Divider
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    marginBottom(20.px)
                    gap(16.px)
                }
            }) {
                Hr({
                    style {
                        property("flex", "1")
                        border(0.px)
                        height(1.px)
                        backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
                    }
                })
                Span({
                    style {
                        color(Color("#64748b"))
                        fontSize(14.px)
                        fontWeight(500)
                    }
                }) { Text("or pay as you go") }
                Hr({
                    style {
                        property("flex", "1")
                        border(0.px)
                        height(1.px)
                        backgroundColor(Color("rgba(255, 255, 255, 0.1)"))
                    }
                })
            }

            H3({
                style {
                    fontSize(18.px)
                    fontWeight(600)
                    color(Color("#FAFAFA"))
                    marginBottom(16.px)
                }
            }) { Text("Buy Credits") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    justifyContent(JustifyContent.Center)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                QuickCreditButton("10 for €7", "pack_10", isLoading, onBuyCredits)
                QuickCreditButton("25 for €15", "pack_25", isLoading, onBuyCredits, isBest = true)
                QuickCreditButton("50 for €25", "pack_50", isLoading, onBuyCredits)
            }
        }

        Button({
            classes("btn", "btn-secondary")
            style {
                padding(12.px, 32.px)
                fontSize(15.px)
            }
            onClick { onBack() }
        }) {
            Span({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                }
            }) {
                Text("←")
                Text("Go Back")
            }
        }
    }
}

@Composable
private fun QuickCreditButton(
    label: String,
    packId: String,
    isLoading: Boolean,
    onBuy: (String) -> Unit,
    isBest: Boolean = false
) {
    Button({
        classes("btn")
        style {
            padding(12.px, 20.px)
            borderRadius(6.px)
            fontSize(14.px)
            fontWeight(600)
            if (isBest) {
                backgroundColor(Color("#FF0000"))
                color(Color.white)
                property("box-shadow", "0 4px 15px rgba(255, 0, 0, 0.25)")
            } else {
                backgroundColor(Color.transparent)
                color(Color("#FAFAFA"))
                property("border", "1px solid rgba(255, 255, 255, 0.2)")
            }
        }
        onClick { onBuy(packId) }
        if (isLoading) disabled()
    }) {
        Text(label)
    }
}
